import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus } from "lucide-react";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import type { PersonnelProject, RateSchedule, TMTimesheet, TMWorker } from "@/hooks/usePersonnelProjects";
import { TimesheetEntryDialog } from "./TimesheetEntryDialog";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const statusEmoji: Record<string, string> = {
  draft: "📋",
  submitted: "📤",
  client_approved: "🔵",
  invoiced: "✅",
  paid: "💰",
};

interface Props {
  project: PersonnelProject;
  timesheets: TMTimesheet[];
  rates: RateSchedule[];
  workers: TMWorker[];
}

export function PersonnelTimesheetsTab({ project, timesheets, rates, workers }: Props) {
  const isMobile = useIsMobile();
  const [entryOpen, setEntryOpen] = useState(false);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-between items-center">
        <h2 className="text-base font-semibold">Timesheets</h2>
        <Button size="sm" onClick={() => setEntryOpen(true)}>
          <Plus className="mr-1 h-3 w-3" /> New
        </Button>
      </div>

      {timesheets.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No timesheets yet</p>
      ) : isMobile ? (
        <div className="space-y-3">
          {timesheets.map((t) => (
            <Card key={t.id}>
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{format(new Date(t.week_ending), "MMM d, yyyy")}</span>
                  <span className="text-xs">
                    {statusEmoji[t.status] ?? ""} {t.status.replace("_", " ")}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {t.total_regular_hours + t.total_ot_hours}h ({t.total_ot_hours} OT)
                  </span>
                  <span className="text-lg font-bold">{fmt(t.total_billed)}</span>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-medium ${t.margin_pct >= 25 ? "text-emerald-600" : t.margin_pct >= 15 ? "text-amber-600" : "text-destructive"}`}>
                    {t.margin_pct.toFixed(1)}% margin
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Week Ending</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">OT</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {timesheets.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{format(new Date(t.week_ending), "MMM d, yyyy")}</TableCell>
                    <TableCell>
                      <span className="mr-1">{statusEmoji[t.status] ?? ""}</span>
                      {t.status.replace("_", " ")}
                    </TableCell>
                    <TableCell className="text-right">{t.total_regular_hours + t.total_ot_hours}</TableCell>
                    <TableCell className="text-right">{t.total_ot_hours}</TableCell>
                    <TableCell className="text-right">{fmt(t.total_billed)}</TableCell>
                    <TableCell className={`text-right font-medium ${t.margin_pct >= 25 ? "text-emerald-600" : t.margin_pct >= 15 ? "text-amber-600" : "text-destructive"}`}>
                      {t.margin_pct.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <TimesheetEntryDialog
        open={entryOpen}
        onOpenChange={setEntryOpen}
        project={project}
        rates={rates}
        workers={workers}
      />
    </div>
  );
}
